import { useState, useRef, useEffect } from 'react';
import { z } from 'zod';
import { request } from '../../lib/oem-client';
const rowSchema = z.object({
  id: z.uuid(),
  version: z.int().positive(),
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  label: z.string().nullable(),
  grade: z.string().nullable(),
  basis: z.string().nullable(),
  dimensions: z.string().nullable(),
  material: z.string().nullable(),
  usagePerSaleUnit: z.string().nullable(),
  constituents: z
    .array(
      z.object({
        inci: z.string().nullable(),
        concentration: z.string().nullable(),
        basis: z.string().nullable(),
      }),
    )
    .nullable(),
});
type Row = z.infer<typeof rowSchema>;
export function StockSourcePicker({
  projectId,
  kind,
  value,
  onChange,
}: {
  projectId: string;
  kind: 'material_spec' | 'component_spec';
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const [projects, setProjects] = useState<{ id: string; label: string }[]>([]),
    [origin, setOrigin] = useState(''),
    [rows, setRows] = useState<Row[]>([]),
    [message, setMessage] = useState('เชื่อมต้นทางได้เฉพาะโครงการที่มีสิทธิ์เทคนิคทั้งสองฝั่ง'),
    [busy, setBusy] = useState(false);
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const selected = z
    .object({ id: z.uuid(), version: z.int().positive(), hash: z.string() })
    .safeParse(value);
  async function loadProjects() {
    setBusy(true);
    try {
      const raw = z
        .object({
          contexts: z.array(
            z.object({
              projectId: z.uuid(),
              label: z.string(),
              audience: z.string(),
              readBundles: z.array(z.string()),
            }),
          ),
        })
        .parse(await request('/session'));
      setProjects(
        raw.contexts
          .filter((c) => c.audience === 'staff' && c.readBundles.includes('technical'))
          .map((c) => ({ id: c.projectId, label: c.label })),
      );
      setMessage(
        'เลือกต้นทางและเทียบข้อมูลจริง ก่อนให้ผู้ทบทวนเทคนิคที่อ่านทั้งสองโครงการตรวจฉบับนี้',
      );
    } catch {
      setMessage('ยังอ่านขอบเขตไม่ได้ · ให้ผู้มีสิทธิ์เทคนิคทั้งสองโครงการตรวจสอบ');
    } finally {
      setBusy(false);
    }
  }
  async function load(id: string) {
    const seq = ++generation.current;
    setOrigin(id);
    setRows([]);
    if (!id) return;
    setBusy(true);
    try {
      const loaded = z
        .array(rowSchema)
        .parse(await request(`/stock-sources/${projectId}/${id}/${kind}`));
      if (seq !== generation.current) return;
      setRows(loaded);
      setMessage('เฉพาะสเปกต้นทางที่ทบทวนแล้ว · ไม่แสดงราคา ต้นทุน หรือสูตร');
    } catch {
      if (seq !== generation.current) return;
      setMessage(
        'ไม่มีสิทธิ์อ่านเทคนิคทั้งสองฝั่ง หรือสเปกต้นทางยังไม่พร้อม · ไม่เพิ่มสิทธิ์ให้อัตโนมัติ',
      );
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  return (
    <fieldset>
      <legend>ต้นทาง stock ที่ต้องผูกผลทบทวนเทคนิค</legend>
      <p>{message}</p>
      <button
        type="button"
        className="action secondary"
        disabled={busy}
        onClick={() => void loadProjects()}
      >
        อ่านโครงการที่มีสิทธิ์
      </button>
      <label>
        โครงการต้นทาง
        <select value={origin} onChange={(e) => void load(e.target.value)}>
          <option value="">
            {selected.success
              ? 'เลือกโครงการเพื่อเปรียบเทียบ · มีต้นทางที่บันทึกแล้ว'
              : 'เลือกโครงการ'}
          </option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        สเปกต้นทางฉบับตรึง
        <select
          value={selected.success ? selected.data.id : ''}
          disabled={busy}
          onChange={(e) => {
            const row = rows.find((r) => r.id === e.target.value);
            onChange(row ? { id: row.id, version: row.version, hash: row.hash } : undefined);
          }}
        >
          <option value="">ใช้สเปกโครงการนี้โดยตรง</option>
          {selected.success && !rows.some((r) => r.id === selected.data.id) ? (
            <option value={selected.data.id}>
              ต้นทางที่บันทึก · {selected.data.id} · v{selected.data.version} · {selected.data.hash}
            </option>
          ) : null}
          {rows.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label ?? kind} · v{r.version} · {r.hash.slice(0, 12)}
            </option>
          ))}
        </select>
      </label>
      {rows
        .filter((r) => selected.success && r.id === selected.data.id)
        .map((r) => (
          <div key={r.id}>
            <p>
              เกรด {r.grade ?? '—'} · ฐาน {r.basis ?? '—'} · ขนาด {r.dimensions ?? '—'} · วัสดุ{' '}
              {r.material ?? '—'}
            </p>
            <ul>
              {r.constituents?.map((c, i) => (
                <li key={i}>
                  {c.inci ?? 'ยังไม่มี INCI'} {c.concentration}% ({c.basis})
                </li>
              ))}
            </ul>
          </div>
        ))}
      {selected.success ? (
        <p>
          ผูกฉบับ {selected.data.version} · {selected.data.hash.slice(0, 12)} ·
          ต้นทางมีฉบับส่งใหม่หรือสิทธิ์ผู้ทบทวนสิ้นสุดจะพักการใช้จนทบทวนใหม่
        </p>
      ) : null}
      <p>
        ตัวระบุวัตถุดิบ/ส่วนประกอบของโครงการนี้คงเดิม · การเชื่อมไม่ได้ให้สิทธิ์ QA
        และใช้ต้นทางข้ามโครงการได้เฉพาะ stock โรงงาน
      </p>
    </fieldset>
  );
}
